import { IUnitServiceHistoryInputDto, ServiceDocumentType, UnitServiceHistoryInputDto } from 'api/api';
import { Dropdown } from 'primereact/dropdown';
import React, { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { createUnitServiceHistory } from 'state/ducks/unit-service-history/operations';
import { localized } from 'state/i18n';
import { toCalendarDay } from 'utilities/date-helpers';
import { useAppDispatch } from 'utilities/hooks';
import BiTextArea from 'view/components/bi-text/bi-textarea';
import { BiFileTypes, BiFileUploader } from 'view/shared/components/bi-file-uploader/bi-file-uploader';
import BiButton from 'view/shared/components/buttons/bi-button/bi-button';
import BiTextDialog from 'view/shared/components/dialogs/bi-text-dialog/bi-text-dialog';
import BiCalendar from '../bi-calendar/bi-calendar';
import './upload-service-history-dialog.scss';

interface Props {
	unitId?: number;
	onSave: (unitServiceHistory: IUnitServiceHistoryInputDto) => void;
	onCancel: () => void;
	visible: boolean;
}

const initialDateOfService = new Date(1, 1, 1);
export const UploadServiceHistoryDialog: FC<Props> = memo(({ unitId, onSave, onCancel, visible }) => {
	const [unitServiceHistory, setUnitServiceHistory] = useState<IUnitServiceHistoryInputDto>(
		new UnitServiceHistoryInputDto()
	);

	const initialState = useMemo<IUnitServiceHistoryInputDto>(
		() => ({
			unitId: unitId ?? 0,
			comment: undefined,
			file: undefined,
			serviceDocumentType: undefined,
			dateOfService: initialDateOfService,
		}),
		[unitId]
	);

	useEffect(() => {
		if (visible) {
			setUnitServiceHistory(initialState);
		}
	}, [visible, initialState]);

	const handleOnCancel = useCallback(() => {
		setUnitServiceHistory(initialState);
		onCancel();
	}, [onCancel, initialState]);

	const dispatch = useAppDispatch();

	const handleSave = useCallback(() => {
		if (unitServiceHistory) {
			const unitServiceHistoryToSave = {
				...unitServiceHistory,
				file: unitServiceHistory.file
					? { data: unitServiceHistory.file, fileName: unitServiceHistory.file.name }
					: undefined,
			};

			createUnitServiceHistory(unitServiceHistoryToSave)(dispatch);
			onSave(unitServiceHistoryToSave);
		}
	}, [unitServiceHistory, dispatch, onSave]);

	const formIsValid = useMemo(
		() =>
			!!unitServiceHistory &&
			!!unitServiceHistory.file &&
			unitServiceHistory.serviceDocumentType !== undefined &&
			unitServiceHistory.dateOfService !== initialDateOfService,
		[unitServiceHistory]
	);

	const handleFileSelect = useCallback(
		(file: File | null) => {
			setUnitServiceHistory({ ...unitServiceHistory, file: file } as IUnitServiceHistoryInputDto);
		},
		[unitServiceHistory]
	);

	const setComment = useCallback(
		(e: React.ChangeEvent<HTMLTextAreaElement>) => {
			setUnitServiceHistory({
				...unitServiceHistory,
				comment: e.currentTarget.value,
			} as IUnitServiceHistoryInputDto);
		},
		[unitServiceHistory]
	);

	const setDateOfService = useCallback(
		(e: { value: Date }) => {
			if (!e.value) {
				setUnitServiceHistory({
					...unitServiceHistory,
					dateOfService: initialDateOfService,
				} as IUnitServiceHistoryInputDto);
			} else {
				const dateOnly = toCalendarDay(e.value);

				setUnitServiceHistory({
					...unitServiceHistory,
					dateOfService: dateOnly,
				} as IUnitServiceHistoryInputDto);
			}
		},
		[unitServiceHistory]
	);

	const setServiceDocumentType = useCallback(
		(e: { value: ServiceDocumentType }) => {
			setUnitServiceHistory({
				...unitServiceHistory,
				serviceDocumentType: e.value,
			} as IUnitServiceHistoryInputDto);
		},
		[unitServiceHistory]
	);

	const serviceDocumentTypeOptions = Object.keys(ServiceDocumentType)
		.filter(key => isNaN(Number(key)))
		.map(key => ({
			label: localized(key as keyof typeof ServiceDocumentType),
			value: ServiceDocumentType[key as keyof typeof ServiceDocumentType],
		}));

	return (
		<>
			<BiTextDialog
				onHide={handleOnCancel}
				visible={visible}
				className="upload-service-history-dialog"
				title={localized('UploadServiceDocument')}
			>
				<div className="p-grid upload-service-history-dialog__field-row">
					<div className="p-col-2 upload-service-history-dialog__label">{localized('DateOfService')}</div>
					<div className="p-col-4">
						<BiCalendar
							value={
								unitServiceHistory?.dateOfService === initialDateOfService
									? undefined
									: unitServiceHistory?.dateOfService
							}
							onChange={setDateOfService}
							maxDate={new Date()}
							showButtonBar
						/>
					</div>
					<div className="p-col-2 upload-service-history-dialog__label">{localized('DocumentType')}</div>
					<div className="p-col-4">
						<Dropdown
							options={serviceDocumentTypeOptions}
							value={unitServiceHistory?.serviceDocumentType}
							onChange={setServiceDocumentType}
							placeholder={localized('SelectType')}
						/>
					</div>
				</div>
				<div className="p-grid upload-service-history-dialog__field-row">
					<div className="p-col-2 upload-service-history-dialog__label">{localized('Comment')}</div>
					<div className="p-col-10">
						<BiTextArea
							placeholder={localized('InsertCustomMessage')}
							onChange={setComment}
							value={unitServiceHistory?.comment}
						/>
					</div>
				</div>
				<div className="upload-service-history-dialog__uploader">
					<BiFileUploader
						onFileSelect={handleFileSelect}
						file={unitServiceHistory?.file}
						allowedFileTypes={[BiFileTypes.PDF]}
					/>
				</div>

				<div className="upload-service-history-dialog__actions">
					<BiButton
						colorTheme="org-white"
						containerTheme="slim-with-rounded-corners"
						className="cancel-button"
						onClick={handleOnCancel}
					>
						{localized('Cancel')}
					</BiButton>
					<BiButton
						colorTheme="org-green"
						containerTheme="slim-with-rounded-corners"
						onClick={handleSave}
						disabled={!formIsValid}
					>
						{localized('Save')}
					</BiButton>
				</div>
			</BiTextDialog>
		</>
	);
});
